import ReactECharts from 'echarts-for-react';

import { SectionTitle } from '@/components/common/SectionTitle';
import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import {
  buildInventoryCoverageChartOption,
  type ForecastQuantiles,
} from '@/features/decisions/utils/inventoryCoverageChartOption';

interface Props {
  policy: InventoryCoveragePolicy | undefined;
  decisionDate: string;
  planningHorizon: string | null;
  isLoading: boolean;
  error: Error | null;
  quantiles?: ForecastQuantiles;
}

// Inventory Coverage vs Demand for the previewed policy. Available + expected
// receipts vs. inventory consumed at horizon; the reorder on today's stack
// closes the negative inventory position at end of horizon. Decision date is
// controlled by the shared scenario controls bar above.
export function InventoryCoverageChart({ policy, decisionDate, planningHorizon, isLoading, error, quantiles }: Props) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2">
        <SectionTitle>Inventory Coverage vs Demand</SectionTitle>
        <p className="mt-0.5 text-xs text-gray-500">
          Available + expected receipts vs. inventory consumed at horizon. The reorder on today&apos;s stack closes the
          negative inventory position at end of horizon{planningHorizon ? ` (${planningHorizon})` : ''}.
        </p>
      </div>

      {isLoading && <div className="py-6 text-sm text-gray-600">Loading coverage…</div>}
      {error && (
        <div className="py-6 text-sm text-gray-400">
          No coverage data available for this decision period. Try selecting a different SKU or period.
        </div>
      )}

      {!isLoading && !error && (
        <>
          <ReactECharts
            option={buildInventoryCoverageChartOption(policy, decisionDate, quantiles)}
            opts={{ renderer: 'svg' }}
            style={{ height: 360 }}
            notMerge
          />
          <div className="mt-1 text-sm font-medium text-gray-500">① Decision · ② Available · ③ Next available</div>
        </>
      )}
    </div>
  );
}
